import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import sortAccounts from 'utils/api/sortAccounts';
import { selectListAccountsFromConnections, selectSpaceTradingAccounts, selectUserId, selectUserTradingAccounts, } from 'reducers/selectors';
import { selectPotsWithSpaceId, selectSpacePots, } from 'features/pots/reducers/selectors';
import { selectSpaces } from '../reducers/selectors';
const getAccountsFromSpaceTiers = (accounts, proPreviewAccounts, ultimatePreviewAccounts) => {
    const previewAccounts = [
        ...(proPreviewAccounts || []),
        ...(ultimatePreviewAccounts || []),
    ];
    const merged = [
        ...accounts,
        ...previewAccounts.filter((account) => accounts.find((acc) => acc.id === account.id) === undefined),
    ];
    return merged;
};
const emptyPotsArr = [];
const getPotsArray = (spacePots, spaceInfo) => {
    if (!spaceInfo?.space?.id)
        return emptyPotsArr;
    return spacePots?.[spaceInfo.space.id] || emptyPotsArr;
};
const emptyTradingAccounts = [];
const getTradingAccountsArray = (spaceTradingAccounts, spaceInfo) => {
    if (!spaceInfo?.space?.id)
        return emptyTradingAccounts;
    return spaceTradingAccounts?.[spaceInfo.space.id] || emptyTradingAccounts;
};
export const useSpaceAccounts = (spaceInfo) => {
    const myId = useAppSelector(selectUserId);
    const accountsFromConnections = useAppSelector(selectListAccountsFromConnections);
    const spaces = useAppSelector(selectSpaces);
    const pots = useAppSelector(selectPotsWithSpaceId);
    const spacePots = useAppSelector(selectSpacePots);
    const spaceTradingAccounts = useAppSelector(selectSpaceTradingAccounts);
    const userTradingAccounts = useAppSelector(selectUserTradingAccounts);
    const defaultSpaceId = useMemo(() => {
        const defaultSpace = spaces.find((space) => space.isDefault);
        return defaultSpace?.id;
    }, [spaces]);
    const accounts = useMemo(() => {
        const merged = getAccountsFromSpaceTiers(accountsFromConnections, spaceInfo?.proPreviewAccounts, spaceInfo?.ultimatePreviewAccounts);
        return sortAccounts([
            ...merged,
            ...pots,
            ...(userTradingAccounts || []),
        ]);
    }, [accountsFromConnections, pots, spaceInfo, userTradingAccounts]);
    const currentSpaceAccounts = useMemo(() => {
        const id = spaceInfo?.space?.id;
        if (id && spaceInfo.accounts) {
            const accs = getAccountsFromSpaceTiers(spaceInfo.accounts, spaceInfo?.proPreviewAccounts, spaceInfo?.ultimatePreviewAccounts);
            const result = accs && id
                ? accs?.map((it) => ({
                    ...it,
                    spaceId: id,
                }))
                : accounts.filter(({ spaceId }) => spaceId === id);
            const selectedPots = getPotsArray(spacePots, spaceInfo);
            const selectedTradingAccounts = getTradingAccountsArray(spaceTradingAccounts, spaceInfo);
            return [...result, ...selectedPots, ...selectedTradingAccounts];
        }
        return [];
    }, [accounts, spaceInfo, spacePots, spaceTradingAccounts]);
    const [selectedAccounts, setSelectedAccounts] = useState(currentSpaceAccounts.filter((acc) => acc.userId === myId));
    const accountsToDisplay = useMemo(() => {
        if (!spaces)
            return [];
        const result = spaceInfo
            ? [{ space: spaceInfo.space, accounts: selectedAccounts }]
            : [];
        const filteredSpaces = spaces.filter(({ id }) => id !== spaceInfo?.space.id);
        const selectedIds = selectedAccounts.map(({ id }) => id);
        const filteredAccounts = accounts.filter(({ id }) => !selectedIds.includes(id));
        filteredSpaces.forEach((it) => {
            const items = filteredAccounts.filter(({ spaceId }) => spaceId === it.id ||
                (it.isDefault && spaceId == null) ||
                (it.isDefault && spaceId === spaceInfo?.space.id));
            if (items.length > 0) {
                result.push({ space: it, accounts: items });
            }
        });
        return result;
    }, [accounts, selectedAccounts, spaceInfo, spaces]);
    const onSelected = useCallback((index, subIndex) => {
        setSelectedAccounts((items) => {
            if (accountsToDisplay[index].accounts[subIndex].userId !== myId)
                return items;
            if (index === 0) {
                items.splice(subIndex, 1);
                return [...items];
            }
            return [...items, accountsToDisplay[index].accounts[subIndex]];
        });
    }, [accountsToDisplay, myId]);
    const defaultSpaceAccounts = useMemo(() => accounts.filter(({ spaceId }) => spaceId == null || spaceId === defaultSpaceId), [accounts, defaultSpaceId]);
    return {
        accounts: accountsToDisplay,
        onSelected,
        currentSpaceAccounts,
        defaultSpaceAccounts,
    };
};
